import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import { Inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, ReplaySubject } from 'rxjs';
import { CrmObjectService, SystemFieldIds } from './crm-services';
import { CrmDependencies, CrmInjectionToken, UserWithDisplayName } from '../tokens-and-interfaces';
import { convertToReadableUsers } from '../utils';
import { catchError, filter, shareReplay, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class ActivityAssigneeService {
  private readonly allUsers$$: ReplaySubject<UserWithDisplayName[]> = new ReplaySubject(1);
  public readonly allUsers$ = this.allUsers$$.asObservable();

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(IAMService) private readonly iamService: IAMService,
    private readonly crmObjectService: CrmObjectService,
  ) {
    this.config.namespace$
      .pipe(
        filter((id) => !!id),
        takeUntilDestroyed(),
        switchMap((namespace) =>
          this.crmObjectService.listFieldOptions('Activity', {
            namespace: namespace,
            fieldId: SystemFieldIds.ActivityOwnerID,
            pageSize: 100,
          }),
        ),
        map((resp) => resp.options ?? []),
        map((userIds) => userIds.map((userId) => new UserIdentifier({ userId }))),
        switchMap((userIds) => this.iamService.getMultiUsers(userIds)),
        map((users) => convertToReadableUsers(users)),
        catchError((error) => {
          console.error(error);
          return of([]);
        }),
      )
      .subscribe((users) => this.allUsers$$.next(users));
  }

  getMultiUsers(userIds: string[]): Observable<UserWithDisplayName[]> {
    const cachedUser$ = this.allUsers$.pipe(
      map((users) => {
        const userHashMap = new Map<string, UserWithDisplayName | undefined>();
        userIds.forEach((userId) =>
          userHashMap.set(
            userId,
            users.find((u) => u.userId === userId),
          ),
        );
        return userHashMap;
      }),
      shareReplay(1),
    );

    const fetchedMissingUsers$ = cachedUser$.pipe(
      map((cachedUsers) => userIds.filter((userId) => cachedUsers.get(userId) === undefined)),
      switchMap((idsToGet) => {
        if (idsToGet.length === 0) {
          return of([] as User[]);
        }
        return this.iamService.getMultiUsers(idsToGet.map((userId) => new UserIdentifier({ userId })));
      }),
      catchError((error) => {
        console.error(error);
        return of([] as User[]);
      }),
      map((users) => convertToReadableUsers(users)),
    );

    return combineLatest([cachedUser$, fetchedMissingUsers$]).pipe(
      map(([cachedUsers, fetchedUsers]) => {
        fetchedUsers.forEach((user) => cachedUsers.set(user.userId, user));
        return [...cachedUsers.values()].filter(Boolean) as UserWithDisplayName[];
      }),
    );
  }
}
